import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

const FALLBACK_SOL_USD = 85;
const REFRESH_INTERVAL_MS = 60000;

interface SolPriceState {
  price: number;
  lastUpdated: Date | null;
  isLoading: boolean;
  error: string | null;
}

export function useSolPrice() {
  const [state, setState] = useState<SolPriceState>({
    price: FALLBACK_SOL_USD,
    lastUpdated: null,
    isLoading: true,
    error: null
  });

  const fetchPrice = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('fetch-sol-price');

      if (error) throw new Error(error.message);

      if (data?.price && data.price > 0) {
        setState({
          price: data.price,
          lastUpdated: new Date(),
          isLoading: false,
          error: null,
        });
        return data.price;
      }
      throw new Error(data?.error || 'Invalid price');
    } catch (err: any) {
      console.warn('[useSolPrice] Edge function failed, trying direct fallback:', err.message);
      // Direct fallback to Binance
      try {
        const res = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT', {
          signal: AbortSignal.timeout(5000),
        });
        if (res.ok) {
          const d = await res.json();
          const p = parseFloat(d?.price);
          if (p > 0) {
            setState({ price: p, lastUpdated: new Date(), isLoading: false, error: null });
            return p;
          }
        }
      } catch {}

      console.warn('[useSolPrice] All sources failed, using fallback:', FALLBACK_SOL_USD);
      setState((prev) => ({ ...prev, isLoading: false, error: 'All price sources failed' }));
      return state.price;
    }
  }, [state.price]);

  useEffect(() => { fetchPrice(); }, []);

  useEffect(() => {
    const interval = setInterval(fetchPrice, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchPrice]);

  const usdToSol = useCallback((usdAmount: number): number => {
    if (state.price <= 0) return 0;
    return usdAmount / state.price;
  }, [state.price]);

  const solToUsd = useCallback((solAmount: number): number => {
    return solAmount * state.price;
  }, [state.price]);

  return {
    ...state,
    refresh: fetchPrice,
    usdToSol,
    solToUsd,
    formattedPrice: `$${state.price.toFixed(2)}`
  };
}
